import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { AppButtonComponent } from '../components/button/app-button.component';
import { AuthService } from '../../core/services/auth.service';
import { UiService } from '../../core/services/ui.service';
import { Subject, combineLatest, map, Observable } from 'rxjs';
import { filter, takeUntil, startWith, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { MobileDrawerComponent } from '../components/mobile-drawer/mobile-drawer.component';
import { AdminLearnerToggleComponent } from '../../shared/components/admin-learner-toggle/admin-learner-toggle.component';
import { normalizeRole, ERole } from '../../core/auth/roles';
import { SchoolSignupStateService } from '../../core/services/school-signup-state.service';
import { environment } from '../../../environments/environment';
import { logCritical } from '../../core/logging/logger.service';
import { navigateOffVanityToMainPortal, parseVanitySlugFromLocation } from '../../core/utils/vanity-url.util';
import { MIGRATION_PAGE_PATH } from '../../core/migration/migration-routing.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, AppButtonComponent, MobileDrawerComponent, AdminLearnerToggleComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  isMembershipFlowActive = false;
  isDrawerOpen = false;
  /** Spinner state for the TLL "Log In" button while the full-page hand-off to hosted B2C is in flight. */
  isLoggingIn = false;
  isSchoolSignupEnabled =
    (environment.features?.groupSelfSignup ?? false) && !parseVanitySlugFromLocation();
  /** On the LearnWorlds migration page, which is itself the sign-in step: no "Log In" button there. */
  isOnMigrationPage = false;
  /** Guest on an organization vanity host: membership signup stays on the main portal only. */
  isVanityHost = !!parseVanitySlugFromLocation();
  readonly logoPath = environment.brand === 'tll' ? '../../../assets/TLL.svg' : '../../../assets/SimpleK12.svg';
  readonly logoAlt = environment.brand === 'tll' ? 'TLL Logo' : 'SimpleK12 Logo';
  private destroy$ = new Subject<void>();

  isGroupAdmin$!: Observable<boolean>;

  constructor(
    public authService: AuthService,
    private uiService: UiService,
    private router: Router,
    private stateService: SchoolSignupStateService,
  ) {
    this.isGroupAdmin$ = combineLatest([
      this.authService.isAuthenticated$.pipe(startWith(false)),
      this.authService.userProfile$.pipe(startWith(null)),
    ]).pipe(
      map(([isAuth, profile]) => {
        const role = normalizeRole(profile?.role ?? null);
        return !!isAuth && (role === ERole.GroupAdmin || role === ERole.SubGroupAdmin);
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const url = this.router.url;
        // During the LearnWorlds migration "Log In" can navigate inside the SPA (to the migration page)
        // instead of leaving it, so the spinner must not outlive the navigation.
        this.isLoggingIn = false;
        this.isOnMigrationPage = url.split('?')[0] === MIGRATION_PAGE_PATH;
        this.isMembershipFlowActive =
          url.includes('/register') ||
          url.includes('/pre-register') ||
          url.includes('/choose-memberships') ||
          url.includes('/upgrade');
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * The "Log In" button starts a full-page hand-off to hosted B2C and flips `isLoggingIn` on. If the
   * user hits the browser Back button, the page is restored from the back-forward cache with that flag
   * frozen as true, leaving the spinner stuck. `pageshow` fires on that restore (and on every load), so
   * clear it here to make the button usable again.
   */
  @HostListener('window:pageshow')
  onPageShow(): void {
    this.isLoggingIn = false;
  }

  goToContactUs(): void {
    this.uiService.openContactModal();
  }
  signUp(): void {
    // Both SK12 and TLL go through membership selection first: pick a plan → registration → auto-login →
    // checkout. TLL's hosted B2C sign-up (Option 1) is triggered later by the register step (which stashes
    // the checkout continuation before redirecting), so the flow is identical to SK12 from here.
    this.uiService.setRegistrationFlow(true);
    this.uiService.setPreviousRoute(this.router.url);
    if (navigateOffVanityToMainPortal('/choose-memberships/register')) {
      return;
    }
    this.router.navigate(['/choose-memberships/register']);
  }
  logIn(): void {
    // TLL signs in via a full-page redirect to the hosted B2C page — show a spinner on the button while
    // the browser navigates away. SK12 opens an in-app modal (no redirect), so it stays un-spun.
    if (this.isLoggingIn) {
      return;
    }
    if (environment.features?.redirectLogin) {
      this.isLoggingIn = true;
    }
    this.uiService.openSignInModal();
  }
  createSchoolAccount(): void {
    this.stateService.startNewFlow();
    if (navigateOffVanityToMainPortal('/school-signup/step1')) {
      return;
    }
    this.router.navigate(['/school-signup/step1']);
  }
  goHome(): void {
    this.router.navigate(['/']);
  }
  toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
  }
  logout(): void {
    // TLL redirect/SSO flow: end the B2C session too (full-page hand-off, no local nav needed).
    if (environment.features?.redirectLogin) {
      this.authService.logoutRedirect();
      return;
    }
    this.authService.logout().subscribe({
      next: () => this.router.navigate(['/']),
      error: (error) => logCritical('shared/header/header.component', 'Logout error:', error),
    });
  }
}